import { useState } from 'react';
import { useLang } from '@/hooks/useLang';

const copy = {
  EN: {
    tag: 'Services',
    title: 'What We Build',
    sub: 'From concept to delivery — entirely AI-powered.',
    items: [
      {
        id: 'films',
        icon: 'ri-film-line',
        title: 'AI Films',
        desc: 'Cinematic storytelling powered by generative AI. From short films to full-length features.',
        tags: ['Narrative', 'Documentary', 'Brand Film'],
        comingSoon: false,
      },
      {
        id: 'series',
        icon: 'ri-tv-2-line',
        title: 'AI Series',
        desc: 'Vertical and traditional formats. Built for streaming, social, and enterprise.',
        tags: ['Vertical', 'Traditional', 'Episodic'],
        comingSoon: false,
      },
      {
        id: 'training',
        icon: 'ri-graduation-cap-line',
        title: 'Training Content',
        desc: 'Scalable learning experiences. Multi-language, AI-voiced, and always on-brand.',
        tags: ['E-Learning', 'Corporate', 'Multi-Language'],
        comingSoon: false,
      },
      {
        id: 'games',
        icon: 'ri-gamepad-line',
        title: 'Games',
        desc: 'AI-native interactive game experiences. The next frontier of entertainment.',
        tags: ['Games', 'Interactive', 'AI-Native'],
        comingSoon: true,
      },
      {
        id: 'applications',
        icon: 'ri-apps-line',
        title: 'Applications',
        desc: 'Intelligent AI-powered applications built for scale and real-world impact.',
        tags: ['Apps', 'AI Tools', 'Platforms'],
        comingSoon: false,
      },
    ],
    soon: 'Coming Soon',
    learnMore: 'Learn More',
  },
  TR: {
    tag: 'Hizmetler',
    title: 'Ne Üretiyoruz',
    sub: 'Fikirden teslimata — tamamen yapay zeka destekli.',
    items: [
      {
        id: 'films',
        icon: 'ri-film-line',
        title: 'Yapay Zeka Filmleri',
        desc: 'Üretken yapay zeka ile sinematik hikaye anlatımı. Kısa filmlerden uzun metrajlara.',
        tags: ['Anlatı', 'Belgesel', 'Marka Filmi'],
        comingSoon: false,
      },
      {
        id: 'series',
        icon: 'ri-tv-2-line',
        title: 'Yapay Zeka Dizileri',
        desc: 'Dikey ve geleneksel formatlar. Yayın, sosyal medya ve kurumsal kullanım için.',
        tags: ['Dikey', 'Geleneksel', 'Bölümlü'],
        comingSoon: false,
      },
      {
        id: 'training',
        icon: 'ri-graduation-cap-line',
        title: 'Eğitim İçerikleri',
        desc: 'Ölçeklenebilir öğrenme deneyimleri. Çok dilli, yapay zeka seslendirmeli.',
        tags: ['E-Öğrenme', 'Kurumsal', 'Çok Dilli'],
        comingSoon: false,
      },
      {
        id: 'games',
        icon: 'ri-gamepad-line',
        title: 'Oyunlar',
        desc: 'Yapay zeka destekli etkileşimli oyun deneyimleri. Eğlencenin yeni sınırı.',
        tags: ['Oyunlar', 'Etkileşimli', 'Yapay Zeka'],
        comingSoon: true,
      },
      {
        id: 'applications',
        icon: 'ri-apps-line',
        title: 'Uygulamalar',
        desc: 'Ölçek ve gerçek dünya etkisi için inşa edilmiş akıllı yapay zeka uygulamaları.',
        tags: ['Uygulamalar', 'Yapay Zeka Araçları', 'Platformlar'],
        comingSoon: false,
      },
    ],
    soon: 'Yakında',
    learnMore: 'Daha Fazla',
  },
};

export default function WhatWeBuildSection() {
  const lang = useLang();
  const t = copy[lang];
  const [active, setActive] = useState<string | null>(null);

  return (
    <section id="what-we-build" className="py-32 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-8 md:px-16">
        {/* Header */}
        <div className="max-w-2xl mb-20">
          <span className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-3 block">{t.tag}</span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight mb-4" style={{ fontFamily: '\'Space Grotesk\', sans-serif' }}>
            {t.title}
          </h2>
          <p className="text-gray-400 text-base leading-relaxed">{t.sub}</p>
        </div>

        {/* Cards — 5 items: first row 3, second row 2 centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
          {t.items.slice(0, 3).map((item) => (
            <div
              key={item.id}
              className={`relative bg-white rounded-2xl p-8 border transition-all duration-300 cursor-pointer group ${
                active === item.id ? 'border-gray-300' : 'border-gray-100 hover:border-gray-200'
              } ${item.comingSoon ? 'opacity-70' : ''}`}
              onMouseEnter={() => setActive(item.id)}
              onMouseLeave={() => setActive(null)}
            >
              {item.comingSoon && (
                <span className="absolute top-5 right-5 px-2.5 py-1 bg-gray-100 text-gray-400 text-xs font-semibold rounded-full tracking-wide">
                  {t.soon}
                </span>
              )}
              <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-6 group-hover:bg-gray-900 group-hover:border-gray-900 transition-all duration-300">
                <i className={`${item.icon} text-gray-400 text-xl w-5 h-5 flex items-center justify-center group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: '\'Space Grotesk\', sans-serif' }}>
                {item.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">{item.desc}</p>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-full text-xs text-gray-400 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Second row: 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:w-2/3 lg:mx-auto">
          {t.items.slice(3).map((item) => (
            <div
              key={item.id}
              className={`relative bg-white rounded-2xl p-8 border transition-all duration-300 cursor-pointer group ${
                active === item.id ? 'border-gray-300' : 'border-gray-100 hover:border-gray-200'
              } ${item.comingSoon ? 'opacity-70' : ''}`}
              onMouseEnter={() => setActive(item.id)}
              onMouseLeave={() => setActive(null)}
            >
              {item.comingSoon && (
                <span className="absolute top-5 right-5 px-2.5 py-1 bg-gray-100 text-gray-400 text-xs font-semibold rounded-full tracking-wide">
                  {t.soon}
                </span>
              )}
              <div className="w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-6 group-hover:bg-gray-900 group-hover:border-gray-900 transition-all duration-300">
                <i className={`${item.icon} text-gray-400 text-xl w-5 h-5 flex items-center justify-center group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3" style={{ fontFamily: '\'Space Grotesk\', sans-serif' }}>
                {item.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-6">{item.desc}</p>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 bg-gray-50 border border-gray-100 rounded-full text-xs text-gray-400 font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
